import { useMemo } from 'react';
import { isEmpty } from 'lodash';

import { LinkMapping } from '@dbt-labs/metadata-shared';
import {
  CellContext,
  ColumnDef,
  DropdownOption,
  Icon,
  Link,
  RyeconEyeClosed,
  Tooltip,
} from '@dbt-labs/sourdough';

import { HealthIssueType, SourceHealthIssueType } from '@/__generated__/graphql';
import { PaginatedTable, PaginationType } from '@/components/PaginatedTable';
import { CustomCell } from '@/components/Table/CustomCell';
import { ModelTrustSignalCell } from '@/components/Table/ModelTrustSignalCell';
import { OwnerHeader } from '@/components/Table/OwnerHeader';
import { TimestampContainer } from '@/components/Timestamp';
import { TruncatedCell } from '@/components/TruncatedCell';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { ResourceByEnvironmentParams } from '@/hooks/discovery/resource';
import { useLinkToExplorer } from '@/hooks/links';
import { SearchNode } from '@/hooks/useFuzzySearch';
import { useGetLinkMapping } from '@/hooks/useGetLinkMapping';
import { useTargetEnvironmentType } from '@/hooks/useTargetEnvironmentType';
import { safeParseUniqueId } from '@/typings/parseUniqueId';

type DependentProject = {
  projectId?: number;
  dependentModelsCount?: number;
  defaultEnvironmentId?: number;
  dbtCoreProject?: string;
};

interface PublicModel extends SearchNode {
  projectId: number;
  environmentId: number;
  uniqueId: string;
  runGeneratedAt: string;
  dependentProjects?: DependentProject[];
  lastRunStatus?: string | null;
  healthIssues?: HealthIssueType[];
}

interface PublicModelsTableProps {
  paginationType: PaginationType;
  pageOptions: DropdownOption[];
  pageSize: number;
  isLoading: boolean;
  hasMoreResults: boolean;
  publicModels: any[];
  fetchMore: () => void;
  setSelectedResourceParams: (params: ResourceByEnvironmentParams) => void;
  setIsColumnsPanelVisible: (isVisible: boolean) => void;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  totalCount: number;
  totalPages: number;
}

export const PublicModelsTable: React.FC<PublicModelsTableProps> = ({
  paginationType,
  pageOptions,
  pageSize,
  isLoading,
  hasMoreResults,
  publicModels,
  fetchMore,
  setSelectedResourceParams,
  setIsColumnsPanelVisible,
  currentPage,
  setCurrentPage,
  totalCount,
}) => {
  const { accountId } = useDbtCloudAccount();
  const getTargetEnvironmentType = useTargetEnvironmentType();
  const getDetailsPageLink = useLinkToExplorer('resourceDetails');
  const getProjectLink = useLinkToExplorer('project');
  const getLinkMapping = useGetLinkMapping();

  const columns = useMemo((): ColumnDef<PublicModel, any>[] => {
    return [
      {
        header: 'Name',
        accessorFn: (row: PublicModel) => {
          const uniqueId = safeParseUniqueId(row.uniqueId).ok;
          return (
            <>
              <span>{uniqueId ? uniqueId.remainder : row.uniqueId}</span>
              {!row.hasAccess && (
                <Tooltip
                  className="inline-block text-base font-normal text-fgMain"
                  content="You do not have access to the project that owns this"
                >
                  <Icon
                    testId="noAccessIcon"
                    size="xs"
                    className="ml-1 mt-1 align-text-top text-fgMain"
                    ryecon={RyeconEyeClosed}
                  />
                </Tooltip>
              )}
            </>
          );
        },
        id: 'name',
        cell: ({
          getValue,
          row: {
            original: { projectId, environmentId, uniqueId },
          },
        }: CellContext<PublicModel, any>) => (
          <Link
            isInternal
            to={getDetailsPageLink({
              accountId: accountId ?? 0,
              projectId: projectId,
              environmentType: getTargetEnvironmentType(environmentId),
              dbtUniqueId: uniqueId,
            })}
          >
            {getValue()}
          </Link>
        ),
      },
      {
        header: 'Health',
        accessorKey: 'healthIssues',
        cell: (
          cellProps: CellContext<
            PublicModel,
            (HealthIssueType | SourceHealthIssueType)[]
          >,
        ) => {
          const values = cellProps.getValue();
          const { environmentId, projectId, uniqueId } = cellProps.row.original;
          const environmentType = getTargetEnvironmentType(environmentId);
          /**
           * transform from an array of health issues to a link mapping
           * e.g [HealthIssueType.CautionUpstreamSources] to { CautionUpstreamSources: { to: '...', search: '...' } }
           */
          const linkMapping: LinkMapping = getLinkMapping({
            healthIssues: values,
            projectId,
            environmentType,
            uniqueId,
          });
          return (
            <ModelTrustSignalCell
              {...cellProps}
              additionalMetadata={!isEmpty(linkMapping) ? { linkMapping } : undefined}
            />
          );
        },
        size: 60,
        minSize: 100,
        enableSorting: false,
      },
      {
        header: 'Description',
        accessorKey: 'description',
        id: 'description',
        cell: TruncatedCell,
      },
      // TODO: Change to link cell when/if Public Model has lastRunId
      {
        header: 'Last run',
        accessorFn: (row: PublicModel) =>
          row.runGeneratedAt && (
            <TimestampContainer date={new Date(row.runGeneratedAt)} />
          ),
        id: 'lastRun',
        cell: TruncatedCell,
      },
      {
        header: OwnerHeader,
        accessorKey: 'group',
        id: 'owner',
        cell: ({
          getValue,
          row: {
            original: { environmentId, projectId, uniqueId },
          },
        }) => {
          const parsedUniqueId = safeParseUniqueId(uniqueId);
          const packageName = parsedUniqueId.ok?.packageName;
          const group = getValue();
          return (
            <CustomCell tooltip={group ?? ''}>
              {group && packageName && (
                <Link
                  isInternal
                  to={getDetailsPageLink({
                    accountId: accountId ?? 0,
                    environmentType: getTargetEnvironmentType(environmentId),
                    projectId,
                    dbtUniqueId: `group.${packageName}.${group}`,
                  })}
                >
                  {group}
                </Link>
              )}
            </CustomCell>
          );
        },
      },

      {
        header: 'Columns',
        accessorKey: 'columnCount',
        id: 'columnCount',
        cell: ({
          getValue,
          row: {
            original: { environmentId, uniqueId, hasAccess },
          },
        }: CellContext<PublicModel, any>) => {
          const shouldShowPanelLink = hasAccess && getValue() !== 0;
          return (
            <Tooltip
              displayOnlyWhenTruncated
              content={getValue()}
              className="w-full"
              childrenAreInteractive={true}
            >
              {(ref) => (
                <div
                  ref={ref}
                  className={`z-10 truncate`}
                  onClick={() => {
                    if (!shouldShowPanelLink) return;
                    setSelectedResourceParams({
                      environmentId: environmentId,
                      uniqueId: uniqueId,
                    });
                    setIsColumnsPanelVisible(true);
                  }}
                >
                  {getValue()}
                </div>
              )}
            </Tooltip>
          );
        },
        enableSorting: true,
      },
      {
        header: 'Defined in',
        accessorKey: 'dbtCoreProject',
        id: 'definedIn',
        cell: ({
          getValue,
          row: {
            original: { projectId, environmentId },
          },
        }: CellContext<PublicModel, any>) => (
          <Link
            isInternal
            to={getProjectLink({
              accountId: accountId ?? 0,
              projectId: projectId,
              environmentType: getTargetEnvironmentType(environmentId),
            })}
          >
            {getValue()}
          </Link>
        ),
      },
      {
        header: 'Used by',
        accessorFn: (row: PublicModel) => {
          if (!row.dependentProjects?.length) return null;
          // TODO: Is there a "smart" way to have more than one project without potentially getting truncated?
          const [first, ...remaining] = row.dependentProjects;
          const remainingSuffix = remaining.length ? `, +${remaining.length}` : '';
          return `${first?.dbtCoreProject}${remainingSuffix}`;
        },
        id: 'usedBy',
      },
    ];
  }, [
    accountId,
    getDetailsPageLink,
    getProjectLink,
    getLinkMapping,
    getTargetEnvironmentType,
    setSelectedResourceParams,
    setIsColumnsPanelVisible,
  ]);

  const pageInfo = {
    totalCount: null,
    hasNextPage: hasMoreResults,
    endCursor: null,
  };

  const pageDisplayLabel =
    publicModels?.length == null || pageInfo?.totalCount == null
      ? undefined
      : `Loaded ${publicModels.length} of ${pageInfo.totalCount}`;

  if (!accountId) {
    return null;
  }

  return (
    <PaginatedTable
      paginationType={paginationType}
      columns={columns}
      data={publicModels}
      rowHeight={40}
      isSortable={false}
      loadingRowCount={pageSize}
      isLoading={isLoading}
      maxResultCount={totalCount}
      pageSize={pageSize}
      currentPage={currentPage}
      setCurrentPage={setCurrentPage}
      emptyStateProps={{
        header: `No results`,
        helpText: `No results were found`,
      }}
      pagination={{
        selectedPageCount: pageOptions.find(({ value }) => value === pageSize),
        pageOptions,
        shouldShowLoadMore: !isLoading && pageInfo?.hasNextPage,
        hasMorePages: pageInfo?.hasNextPage,
        onLoadMore: fetchMore,
        isPageLoading: isLoading,
        pageDisplayLabel,
      }}
    />
  );
};
